import { AddressValidationStatus } from "@omni/types/api/customer/cloud/shared/enums/addressValidationStatus.enum";
import {
    addMethod,
    boolean,
    mixed,
    StringSchema,
    string as yupString
} from "yup";

import { Translate } from "@/redux/modules/value/translations/translations.provider";
import { checkCountryZipCode } from "@/services/zipCode.service.RHF";

import { string } from "./general.RHF";

addMethod<StringSchema>(yupString, "zipCode", function (required: boolean) {
    return this.test("zipCode", function (value) {
        if (!value) {
            return !required;
        }

        const { parent: formValues } = this;

        const country = formValues[`country`] || null;

        return (
            (value &&
                country &&
                checkCountryZipCode(country.toUpperCase(), value)) ||
            this.createError({
                path: this.path,
                message: () => () => (t: Translate) =>
                    t("general.form.input.validation.zipCode.invalid")
            })
        );
    });
});

addMethod<StringSchema>(yupString, "bPostCheck", function () {
    return this.test("bPostCheck", function () {
        const { parent: formValues } = this;

        const missingElements: Array<string> = [];

        if (!formValues[`selected`]) {
            return this.createError({
                message: () => () => (t: Translate) =>
                    t("general.form.input.validation.noAddressSelected")
            });
        }

        // Check which elements are missing
        if (!formValues[`street`]) {
            missingElements.push("general.form.input.validation.street");
        }
        if (!formValues[`number`]) {
            missingElements.push("general.form.input.validation.number");
        }
        if (!formValues[`zipCode`]) {
            missingElements.push("general.form.input.validation.zipCode");
        }
        if (!formValues[`city`]) {
            missingElements.push("general.form.input.validation.city");
        }
        if (!formValues[`box`] && !formValues[`isComplete`]) {
            missingElements.push("general.form.input.validation.box");
        }

        return (
            missingElements.length === 0 ||
            this.createError({
                message: () => () => (t: Translate) =>
                    t(
                        "general.form.input.validation.address.incomplete",
                        missingElements.map((e) => t(e)).join(", ")
                    )
            })
        );
    });
});

export const companyName = string().nullable().required().max(100);

export const optionalCompanyName = string().nullable().max(100);

export const addressing = string().nullable().max(100);

export const addressLine = string()
    .nullable()
    .when("selected", {
        is: (selected: boolean) => !!selected,
        then: (schema) => schema.required().bPostCheck()
    });

export const street = string().nullable().required().max(64);
export const optionalStreet = string().nullable().max(64).optional();

export const number = string().nullable().required().max(3);
export const optionalNumber = string().nullable().max(3).optional();

export const box = string().nullable().max(10);
export const optionalBox = string().nullable().max(10).optional();

export const zipCode = string().nullable().required().zipCode(true);
export const optionalZipCode = string().nullable().zipCode(false).optional();

export const city = string().nullable().required().max(100);
export const optionalCity = string().nullable().max(100).optional();

export const country = string().nullable().required();
export const optionalCountry = string().nullable();

export const selected = boolean().nullable();

export const validationStatus = mixed<AddressValidationStatus>()
    .oneOf(Object.values(AddressValidationStatus))
    .required();
